import { apiClient } from './api'
import { transformTask, transformTaskForBackend } from '../utils/apiTransformers'
import type { Priority } from './types'

export type TaskStatus = 'todo' | 'in_progress' | 'in_review' | 'done' | 'blocked' | 'cancelled'

export interface Task {
  id: number
  title: string
  description: string | null
  status: TaskStatus
  priority: Priority
  project_id: number
  assignee_id: number | null
  reporter_id: number | null
  start_date: string | null
  due_date: string | null
  estimated_hours: number | null
  actual_hours: number | null
  created_at: string
  updated_at: string
  metadata?: Record<string, unknown>
}

export interface TaskCreate {
  title: string
  description?: string
  status?: TaskStatus
  priority?: Priority
  project_id: number
  assignee_id?: number
  start_date?: string
  due_date?: string
  estimated_hours?: number
  metadata?: Record<string, unknown>
}

export interface TaskUpdate {
  title?: string
  description?: string
  status?: TaskStatus
  priority?: Priority
  assignee_id?: number
  start_date?: string
  due_date?: string
  estimated_hours?: number
  actual_hours?: number
  metadata?: Record<string, unknown>
}

export const tasksService = {
  /**
   * List tasks
   */
  async list(params?: {
    project_id?: number
    assignee_id?: number
    status?: string
    skip?: number
    limit?: number
    include_deleted?: boolean
    /** Custom-field filters, each "key:value" or "key:op:value". */
    custom_field?: string[]
  }): Promise<Task[]> {
    const response = await apiClient.get<unknown[]>('/api/v1/tasks', {
      params,
      // Repeat ?custom_field=… for arrays (FastAPI expects repeated keys).
      paramsSerializer: { indexes: null },
    })
    return response.data.map(transformTask)
  },

  /**
   * Get task by ID
   */
  async get(id: number): Promise<Task> {
    const response = await apiClient.get<unknown>(`/api/v1/tasks/${id}`)
    return transformTask(response.data)
  },

  /**
   * Create task
   */
  async create(data: TaskCreate): Promise<Task> {
    const transformedData = transformTaskForBackend(data)
    const response = await apiClient.post<unknown>('/api/v1/tasks', transformedData)
    return transformTask(response.data)
  },

  /**
   * Update task
   */
  async update(id: number, data: TaskUpdate): Promise<Task> {
    const transformedData = transformTaskForBackend(data)
    const response = await apiClient.put<unknown>(`/api/v1/tasks/${id}`, transformedData)
    return transformTask(response.data)
  },

  /**
   * Delete task
   */
  async delete(id: number): Promise<void> {
    await apiClient.delete(`/api/v1/tasks/${id}`)
  },

  /**
   * Restore a soft-deleted task
   */
  async restore(id: number): Promise<Task> {
    const response = await apiClient.post<unknown>(`/api/v1/tasks/${id}/restore`)
    return transformTask(response.data)
  },
}

